import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api } from "@/client/lib/api";
import { useAuth } from "@/client/lib/auth";
import { AdminShell } from "@/client/components/admin/AdminShell";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { Badge } from "@/client/components/ui/badge";
import { PlanningDeadlineControl } from "@/client/components/admin/PlanningDeadlineControl";
import { KeuzeThresholdControl } from "@/client/components/admin/KeuzeThresholdControl";
import { ShieldCheckIcon, UserIcon } from "lucide-react";

type StaffMember = {
  userId: number;
  personId: number;
  givenName: string;
  familyName: string;
  preferredName: string | null;
  email: string | null;
  role: "admin" | "employee";
  isActive: boolean;
  hasAvatar: boolean;
  lastLoginAt: string | null;
};

// Team management, shown at the top of Instellingen for the beheerder. Each row links to the
// member's detail page (rechten + preview). Admin-only — the page renders this section only for
// the director.
function TeamSection() {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await (api.api.admin as any).staff.$get();
        if (res.ok) {
          const data = await res.json();
          setStaff(data.staff ?? []);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold">Team</h2>
        <p className="text-sm text-muted-foreground">
          {loading ? "Laden..." : `${staff.length} ${staff.length === 1 ? "teamlid" : "teamleden"}. Klik op een naam voor rechten.`}
        </p>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 w-full rounded-lg bg-muted" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-muted-foreground">Kan het team niet laden. Probeer het later opnieuw.</p>
      ) : staff.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nog geen teamleden.</p>
      ) : (
        <div className="w-full overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <th className="px-4 py-2.5 font-medium text-muted-foreground">Naam</th>
                <th className="px-4 py-2.5 font-medium text-muted-foreground">E-mail</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Rol</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((m) => {
                const name = m.preferredName || [m.givenName, m.familyName].filter(Boolean).join(" ");
                return (
                  <tr key={m.userId} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/admin/team/${m.userId}`}
                        className="flex items-center gap-3 text-foreground hover:text-primary hover:underline"
                      >
                        {m.hasAvatar ? (
                          <img src={`/api/avatars/${m.personId}`} alt="" className="size-8 shrink-0 rounded-full object-cover" />
                        ) : (
                          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted">
                            <UserIcon className="size-4 text-muted-foreground" />
                          </span>
                        )}
                        <span className="font-medium">{name}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{m.email ?? "Geen e-mail"}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-center">
                        {m.role === "admin" ? (
                          <Badge variant="outline" size="sm" className="border-violet-300 text-violet-700">
                            <ShieldCheckIcon className="mr-1 size-3" /> Beheerder
                          </Badge>
                        ) : (
                          <Badge variant="outline" size="sm">Medewerker</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-center">
                        {m.isActive ? (
                          <Badge variant="outline" size="sm" className="border-emerald-300 text-emerald-700">Actief</Badge>
                        ) : (
                          <Badge variant="outline" size="sm" className="border-amber-300 text-amber-700">Inactief</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Instellingen — one place for the team (beheerder only) and the KUVO-wide configuration. Replaces
// the separate Configuratie + Team pages so settings live together instead of two thin pages.
export function InstellingenPage() {
  const { section } = useParams<{ section: string }>();
  const { user } = useAuth();
  const isAdmin = !!user?.roles.includes("admin");

  if (!section || !["team", "configuratie"].includes(section)) {
    return <Navigate to={`/admin/instellingen/${isAdmin ? "team" : "configuratie"}`} replace />;
  }

  return (
    <AdminShell>
      <AdminPageHeader
        items={[{ label: "Instellingen" }]}
        subtitle={section === "team" ? "Beheer je team en hun rechten." : "Instellingen die voor alle scholen gelden."}
      />

      <div className="mt-6">
        {section === "team" && isAdmin && <TeamSection />}
        {section === "configuratie" && (
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <KeuzeThresholdControl />
            <PlanningDeadlineControl />
          </div>
        )}
      </div>
    </AdminShell>
  );
}
